import type {
  CourseRegistrationBatchStatusGridItemViewModel,
  EScholarshipStatus,
} from '@/shared/api/generated';
import { useGetApiV1CourseregistrationEnrollmentBatchStatusGrid } from '@/shared/api/generated';

import type { EnrollmentScholarshipStatus, EnrollmentStatus } from '../types/enrollment';
import {
  ENROLLMENT_CODE_TO_STATUS,
  ENROLLMENT_STATUS_TO_CODE,
} from '../types/enrollmentStatusCode';
import type { BatchEligibleStudent } from '../ui/templates/EnrollmentBatchStatusTemplate';

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0] ?? '';
  const last = parts[parts.length - 1] ?? '';
  if (parts.length <= 1) return first.slice(0, 2).toUpperCase();
  return `${first[0] ?? ''}${last[0] ?? ''}`.toUpperCase();
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('pt-BR');
}

/**
 * Mapeia o status de bolsa (enum `EScholarshipStatus` da API) para a abrangência
 * exibida na tabela. O endpoint de lote não retorna o percentual de desconto, então
 * não é possível distinguir bolsa parcial de integral com precisão: bolsistas
 * efetivados (`3`) são exibidos como `full`, candidatos em fluxo (`1`, `2`) como
 * `partial` e os demais (recusados / sem bolsa) como `none`.
 */
function mapScholarship(status: EScholarshipStatus | undefined): EnrollmentScholarshipStatus {
  if (status === 3) return 'full';
  if (status === 1 || status === 2) return 'partial';
  return 'none';
}

function mapItem(item: CourseRegistrationBatchStatusGridItemViewModel): BatchEligibleStudent {
  const name = item.personName ?? '';
  return {
    id: item.courseRegistrationId ?? 0,
    initials: getInitials(name),
    name,
    enrollmentDate: formatDate(item.date),
    attendance: item.avgAttendance ?? null,
    gradeAvg: item.avgGrade ?? null,
    gradeExam: item.finalExamGrade ?? null,
    scholarshipStatus: mapScholarship(item.scholarshipStatus),
    hasMonograph: item.monographDefGrade != null,
    currentStatus:
      item.oldStatus !== undefined
        ? (ENROLLMENT_CODE_TO_STATUS[item.oldStatus] ?? 'payment_selection')
        : 'payment_selection',
  };
}

/**
 * Hook para listar os alunos elegíveis a uma alteração de status em lote e derivar
 * os totais exibidos no resumo da tela.
 *
 * A query só é executada quando turma e status destino estão selecionados — o
 * endpoint retorna lista vazia caso contrário. Os contadores `directCount` e
 * `revertCount` são derivados no frontend comparando o código do status atual de
 * cada aluno com o código do status destino: avanços no ciclo de vida (`código atual
 * < código destino`) contam como "direto", retrocessos como "reversão".
 *
 * @param classStudentId - ID da turma selecionada; `null` desabilita a query
 * @param targetStatus - Status destino selecionado; `null` desabilita a query
 * @returns Alunos elegíveis mapeados, totais derivados e estado de loading/erro
 *
 * @example
 * ```tsx
 * const { eligibleStudents, totalEligibleCount, isLoading } = useBatchStatusGrid(12, 'active');
 * ```
 */
export function useBatchStatusGrid(
  classStudentId: number | null,
  targetStatus: EnrollmentStatus | null,
) {
  const enabled = classStudentId !== null && targetStatus !== null;
  const targetCode = targetStatus ? ENROLLMENT_STATUS_TO_CODE[targetStatus] : undefined;

  const params =
    classStudentId !== null && targetCode !== undefined
      ? { classStudentId, enrollmentStatus: targetCode }
      : undefined;

  const query = useGetApiV1CourseregistrationEnrollmentBatchStatusGrid(params, {
    query: { enabled },
  });

  const items = enabled ? (query.data ?? []) : [];
  const eligibleStudents = items.map(mapItem);

  let directCount = 0;
  let revertCount = 0;
  if (targetCode !== undefined) {
    for (const item of items) {
      if (item.oldStatus === undefined) continue;
      if (item.oldStatus < targetCode) directCount += 1;
      else if (item.oldStatus > targetCode) revertCount += 1;
    }
  }

  return {
    eligibleStudents,
    totalEligibleCount: eligibleStudents.length,
    directCount,
    revertCount,
    isLoading: enabled && query.isLoading,
    isError: query.isError,
  } as const;
}
